import { useState, useEffect, useMemo, useRef } from 'react';
import { getReportColumnsStorageKey } from '../utilities/reportColumnStorage';

/**
 * Hook to manage column visibility with localStorage persistence for reports.
 *
 * Handles both static column arrays and columns generated asynchronously from
 * fetched data (which start empty and populate after load).
 *
 * @param {string} reportKey - Unique key for the report (e.g. 'ClearanceTimeExport')
 * @param {Array} initialColumns - Full array of Ant Design table column objects
 */
export const useColumnVisibility = (reportKey, initialColumns) => {
  const storageKey = useMemo(() => getReportColumnsStorageKey(reportKey), [reportKey]);

  const allKeys = useMemo(
    () => (initialColumns || []).map((col) => String(col.dataIndex || col.key)),
    [initialColumns]
  );
  // Stable primitive dependency so effects don't re-run on every render (the
  // column array is a fresh reference each render for generated columns).
  const allKeysStr = allKeys.join('|');

  const readStoredKeys = () => {
    if (!reportKey) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.error(`Error reading ${storageKey} from localStorage:`, e);
    }
    return null;
  };

  // Get initial visible column keys
  const [visibleKeys, setVisibleKeys] = useState(() => {
    if (!reportKey) return allKeys;
    const stored = readStoredKeys();
    if (stored && stored.length > 0) {
      const validKeys = stored.filter((k) => allKeys.includes(k));
      if (validKeys.length > 0) return validKeys;
    }
    return allKeys;
  });

  // Columns generated from fetched data are empty on first render and populate
  // after load. Initialize the selection (from storage, else all) the first
  // time real columns become available.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!reportKey) return;
    if (allKeys.length === 0) return; // columns not available yet
    if (initializedRef.current) return;

    const currentValid = visibleKeys.filter((k) => allKeys.includes(k));
    if (currentValid.length === 0) {
      const stored = readStoredKeys();
      const fromStored = stored ? stored.filter((k) => allKeys.includes(k)) : [];
      setVisibleKeys(fromStored.length > 0 ? fromStored : allKeys);
    }
    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeysStr]);

  // Save to localStorage when visibleKeys changes. Never persist an empty
  // selection — that only happens transiently before columns load and would
  // otherwise wipe the user's saved selection.
  useEffect(() => {
    if (!reportKey) return;
    if (!visibleKeys || visibleKeys.length === 0) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
    } catch (e) {
      console.error(`Error saving ${storageKey} to localStorage:`, e);
    }
  }, [storageKey, visibleKeys]);

  // Derived filtered columns for Ant Design Table & Excel export
  const filteredColumns = useMemo(() => {
    return (initialColumns || []).filter((col) => {
      const key = String(col.dataIndex || col.key);
      return visibleKeys.includes(key);
    });
  }, [initialColumns, visibleKeys]);

  // Reset to show all columns
  const resetToDefault = () => {
    setVisibleKeys(allKeys);
  };

  return {
    visibleKeys,
    setVisibleKeys,
    filteredColumns,
    resetToDefault,
  };
};

export default useColumnVisibility;
